import { RE2JS } from 're2js';

/**
 * @name formatCardNumber
 * @public
 * @description Formats a credit card number with proper spacing based on card brand.
 * For Amex (003), formats as "xxxx xxxxxx xxxxx".
 * For other cards, formats as "xxxx xxxx xxxx xxxx".
 * @param {string} cardNumber - The credit card number to format
 * @param {string} cardBrand - The brand code of the credit card (e.g., '003' for Amex)
 * @returns {string} Formatted card number with appropriate spaces
 */
export function formatCardNumber(cardNumber, cardBrand) {
  if (cardBrand === '003') {
    if (cardNumber.length > 4 && cardNumber.length <= 10) {
      return cardNumber.replace(/(\d{1,4})/, '$1 ');
    } if (cardNumber.length > 10 && cardNumber.length < 15) {
      return cardNumber.replace(/(\d{1,4})(\d{1,6})/, '$1 $2 ');
    } if (cardNumber.length >= 15) {
      return cardNumber.replace(/\b(\d{4})(\d{6})(\d{5})\b/, '$1 $2 $3');
    }
    return cardNumber;
  }

  const parts = [];
  const tempFormat = cardNumber
    .replace(/\s+/g, '')
    .replace(/[^0-9]/gi, '')
    .substring(0, 16);

  for (let i = 0; i < tempFormat.length; i += 4) {
    parts.push(tempFormat.substr(i, 4));
  }
  return parts.length > 1 ? parts.join(' ') : cardNumber;
}

/**
 * @name getCardBrand
 * @public
 * @description Determines credit card brand based on card number using regex patterns.
 * @param {string} cardNumber - The credit card number to check
 * @param {Array<Object>} regexArr - Array of objects containing regex patterns and brand codes
 * @returns {string} Card brand code (defaults to '021' if no match)
 */
export function getCardBrand(cardNumber, regexArr) {
  // Look through regexArr and return the index of the KV pair
  // where the cardNumber matches the regex
  const i = regexArr.findIndex((regexObj) => {
    const regexStrValue = regexObj.regexValue?.source;
    const isMatch = RE2JS.compile(regexStrValue).matcher(cardNumber).lookingAt();
    return isMatch;
  });

  const matchingCardRegex = regexArr[i];

  return (matchingCardRegex && matchingCardRegex.brand) ? matchingCardRegex.brand : '021';
}

/**
 * @name getCardBrandIcon
 * @public
 * @description Maps card brand code to icon identifier name.
 * @param {string} cardBrand - The brand code of the credit card (defaults to '021')
 * @returns {string} Icon identifier for the card brand
 */
export function getCardBrandIcon(cardBrand = '021') {
  return ({
    '001': 'visa',
    '002': 'mastercard',
    '003': 'amex',
    '004': 'discover',
    '007': 'jcb',
    '021': 'default-card',
  })[cardBrand];
}

/**
 * @name getCardMaxLength
 * @public
 * @description Returns the maximum allowed length for a card number based on its brand.
 * @param {string} cardBrand - The brand code of the credit card
 * @returns {number} Maximum length for card number including spaces
 */
export function getCardMaxLength(cardBrand) {
  if (cardBrand === '003') {
    return 17;
  }
  return 19;
}

/**
 * @name maskCardNumber
 * @public
 * @description Masks a credit card number, showing only last 4 digits.
 * @param {string} cardBrand - The brand code of the credit card
 * @param {string} cardNumber - The credit card number to mask
 * @param {string} obscureChar - Character to use for masking (e.g., '*')
 * @returns {string} Masked card number with appropriate spacing
 */
export function maskCardNumber(cardBrand, cardNumber, obscureChar) {
  let lastDigits;

  if (cardNumber.includes(obscureChar)) {
    return cardNumber;
  }

  if (cardBrand && cardNumber && obscureChar) {
    lastDigits = cardNumber.replace(/ /g, '').slice(-4);
    switch (cardBrand) {
      case '003':
        return `${obscureChar + obscureChar + obscureChar + obscureChar} ${
          obscureChar}${obscureChar}${obscureChar}${obscureChar}${obscureChar}${
          obscureChar} ${obscureChar}${lastDigits}`;
      default:
        return `${obscureChar + obscureChar + obscureChar + obscureChar} ${
          obscureChar}${obscureChar}${obscureChar}${obscureChar} ${
          obscureChar}${obscureChar}${obscureChar}${obscureChar} ${lastDigits}`;
    }
  }
  return cardNumber; // default - lint wanted something here
}

/**
 * @name formatExpirationDate
 * @public
 * @description Formats card expiration date in MM/YY format.
 * @param {string} expirationDate - The expiration date to format
 * @returns {string} Formatted expiration date (MM/YY)
 */
export function formatExpirationDate(expirationDate) {
  if (!expirationDate) {
    return expirationDate;
  }
  if (expirationDate && expirationDate.length > 2 && (expirationDate !== 'undefined' || expirationDate !== undefined)) {
    return expirationDate
      .replace(/\//g, '')
      .substring(0, 2)
      + (expirationDate.length > 2 ? '/' : '')
      + expirationDate
        .replace(/\//g, '')
        .substring(2, 4);
  }
  return expirationDate;
}

/**
 * @name isCardNumberInvalid
 * @public
 * @description Checks if a card number is invalid based on length and brand.
 * @param {string} cardNumber - The credit card number to validate
 * @param {string} cardBrand - The brand code of the credit card
 * @returns {boolean} True if card number is invalid, false otherwise
 */
export function isCardNumberInvalid(cardNumber, cardBrand) {
  if (
    !cardNumber
    || (cardBrand === '003' && cardNumber.length < 15)
    || (cardBrand !== '003' && cardNumber.length < 16)
  ) {
    return true;
  }

  return false;
}

/**
 * @name isExpDateInvalid
 * @public
 * @description Validates if an expiration date is valid and not expired.
 * @param {string} expDate - Expiration date in MMYY format
 * @returns {boolean} True if expiration date is invalid or expired, false otherwise
 */
export function isExpDateInvalid(expDate) {
  if (!expDate || expDate.length < 4) return true;

  const today = new Date();
  const someday = new Date();
  const month = expDate.substring(0, 2);
  const year = expDate.substring(2, 4);
  someday.setFullYear(`20${year}`, month);

  if (someday < today) return true;

  return false;
}

/**
 * @name findBadParams
 * @public
 * @description Extracts card validation errors from API response errors.
 * @param {Array<Object>} responseErrors - Array of error objects from API response
 * @returns {Object} Object with flags for invalid card number and expiration date
 */
export function findBadParams(responseErrors) {
  if (!responseErrors) {
    return { isCardNumberInvalid: false, isCardExpDateInvalid: false };
  }

  return responseErrors.reduce((acc, { extensions = {} }) => {
    const { response } = extensions;

    if (response?.body?.error) {
      response.body.error.forEach((errorItem) => {
        if (errorItem?.errorKey === 'AUTH_MESSAGE1006') {
          acc.isCardExpDateInvalid = true;
        }
      });
    }

    if (response?.body?.Code === '4009540') {
      acc.isCardNumberInvalid = true;
    }

    return acc;
  }, { isCardNumberInvalid: false, isCardExpDateInvalid: false });
}
